import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { CalendarDays, CalendarRange, List, Loader2 } from 'lucide-react';
import type { OfferableSlot, RankedProvider } from '@vagusx/clients';
import { ProviderPoolReasons } from '@/pages/referrals/referral-cells';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { SheetBody } from '@/components/custom/sheet-layout';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { useSimulateAppointmentTypeForTask } from '@/hooks/use-appointment-type-matching';
import { usePatient } from '@/hooks/use-patients';
import { useTask } from '@/hooks/use-tasks';
import { OfferedSlotsWeek } from '@/scheduling/components/OfferedSlotsWeek';
import { useOfferedSlotsRange } from '@/scheduling/hooks/use-offered-slots-range';

/**
 * A Ready referral's providers and the dates Iris could offer them (ADR-341).
 *
 * Left: the Referrals page's "Why these providers?" breakdown of the
 * referral's ranked pool (the same ranking readiness and the scheduling drawer
 * use), with the first provider selected.
 * Right: the selected provider's offered times, as the dates with open
 * times over the next 45 days or as a week calendar: the same offerable-slots
 * engine the voice agent offers from, for the type the provider would be
 * booked as, at the location they were ranked at. A provider tied across
 * types gets a type picker.
 *
 * Which referral is open is the host's to keep (in its URL, as the task id);
 * the patient's name comes from the task, so a reload reopens the same panel.
 */
export function ReadySlotsSheet({
  taskId,
  patientName,
  onClose,
}: {
  /** The referral's task. The sheet is open while there is one. */
  taskId: string | undefined;
  /**
   * The patient's name, when the host already has it (the queue row). Read
   * from the task's patient only when it does not, as after a reload.
   */
  patientName?: string | undefined;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Sheet open={!!taskId} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="w-full gap-0 sm:max-w-5xl">
        {taskId && <SheetHeading taskId={taskId} knownName={patientName} />}
        {/* The two panes scroll on their own, so the dates stay in view
            beside a long pool. Keyed so another referral starts on its first
            provider. */}
        <SheetBody className="grid gap-0 overflow-hidden border-t px-0 pb-0 lg:grid-cols-5">
          {taskId && <ProvidersAndOpenDates key={taskId} taskId={taskId} />}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/** Who the panel is about: the patient's name, from the host or the referral's task. */
export function SheetHeading({
  taskId,
  knownName,
}: {
  taskId: string;
  knownName: string | undefined;
}): React.ReactElement {
  // Nothing is fetched when the host passed the name.
  const { data: task } = useTask(knownName ? undefined : taskId);
  const { data: patient } = usePatient(knownName ? undefined : (task?.patientId ?? undefined));
  const name = knownName ?? (patient ? `${patient.firstName} ${patient.lastName}`.trim() : '');
  return (
    <SheetHeader className="px-6 pt-6 pb-4">
      <SheetTitle>{name || 'Providers and open dates'}</SheetTitle>
      <SheetDescription>Who Iris would offer, and the dates they have open.</SheetDescription>
    </SheetHeader>
  );
}

/**
 * The ranking on the left, the selected provider's open dates on the right.
 *
 * The left is the Referrals page's "Why these providers?" breakdown, the same
 * one the appointment-type simulator renders: the codes the referral was
 * matched on, the providers best first with how they matched, and why they
 * are in that order. Picking a provider there shows their dates.
 */
export function ProvidersAndOpenDates({ taskId }: { taskId: string }): React.ReactElement {
  // The same query the breakdown reads, so this costs no second request.
  const { data, isLoading, isError } = useSimulateAppointmentTypeForTask(taskId, true);
  // A pool row is a provider at a location, so both name the pick.
  const [picked, setPicked] = useState<{ providerId: string; locationId: string } | undefined>(
    undefined
  );
  const [tiedChoice, setTiedChoice] = useState<string | undefined>(undefined);
  // Kept across providers: comparing two providers is easier in one view.
  const [view, setView] = useState<OfferedView>('week');
  // A time clicked in the Dates list: the calendar opens on its week with it
  // highlighted.
  const [focusSlot, setFocusSlot] = useState<OfferableSlot | undefined>(undefined);

  const pool = data?.hasDocument ? data.recommendation.explanation.providers : [];
  const provider =
    pool.find((p) => p.providerId === picked?.providerId && p.locationId === picked.locationId) ??
    pool[0];

  return (
    <>
      <div className="min-h-0 overflow-y-auto border-b p-6 lg:col-span-2 lg:border-r lg:border-b-0">
        <ProviderPoolReasons
          taskId={taskId}
          selectedProviderId={provider?.providerId}
          selectedLocationId={provider?.locationId}
          onSelectProvider={(p) => {
            setPicked({ providerId: p.providerId, locationId: p.locationId });
            setTiedChoice(undefined);
            setFocusSlot(undefined);
          }}
        />
      </div>

      <div className="min-h-0 overflow-y-auto p-6 lg:col-span-3">
        {provider ? (
          <OfferedTimes
            provider={provider}
            typeId={bookingTypeId(provider, tiedChoice)}
            onPickType={setTiedChoice}
            view={view}
            onViewChange={setView}
            focusSlot={focusSlot}
            onOpenSlot={(slot) => {
              setFocusSlot(slot);
              setView('week');
            }}
            isNewPatient={data?.signals.patientType === 'new'}
          />
        ) : isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Ranking the providers…
          </div>
        ) : isError || !data ? (
          <p className="text-sm text-muted-foreground">
            Couldn't rank the providers, so there are no times to show.
          </p>
        ) : !data.hasDocument ? (
          <p className="text-sm text-muted-foreground">
            No referral document linked yet, so there is no pool to show.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            No provider matches this referral, so there are no times to show.
          </p>
        )}
      </div>
    </>
  );
}

/**
 * How far ahead the Dates view looks. Longer than a campaign's slot horizon
 * on purpose: staff read further out than readiness checks.
 */
export const OPEN_DATES_DAYS = 45;

/** How the selected provider's times are shown. */
type OfferedView = 'dates' | 'week';

/**
 * One provider's offered times, two ways: the scheduling drawer's week
 * calendar (the default), or the dates with open times over the next
 * {@link OPEN_DATES_DAYS} days, each with its times. Clicking a time opens the
 * calendar on its week with that slot highlighted.
 */
function OfferedTimes({
  provider,
  typeId,
  onPickType,
  view,
  onViewChange,
  focusSlot,
  onOpenSlot,
  isNewPatient,
}: {
  provider: RankedProvider;
  typeId: string | null;
  onPickType: (typeId: string) => void;
  view: OfferedView;
  onViewChange: (view: OfferedView) => void;
  /** The slot the calendar opens on and highlights. */
  focusSlot: OfferableSlot | undefined;
  /** Open the calendar on this slot's week, highlighted. */
  onOpenSlot: (slot: OfferableSlot) => void;
  isNewPatient: boolean;
}): React.ReactElement {
  const { data: types } = useAppointmentTypes();
  // Fixed for the panel's life, so the query key does not move under it.
  const [startDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const { data, isLoading, isError } = useOfferedSlotsRange(
    {
      providerId: provider.providerId,
      appointmentTypeId: typeId ?? '',
      locationId: provider.locationId,
      startDate,
      days: OPEN_DATES_DAYS,
      isNewPatient,
    },
    // The week calendar asks for its own week; only the dates view needs this.
    !!typeId && view === 'dates'
  );
  const slots = data?.slots;
  const days = useMemo(() => groupByDay(slots ?? []), [slots]);
  const typeName =
    provider.tiedTypes.find((t) => t.appointmentTypeId === typeId)?.appointmentTypeName ??
    provider.appointmentTypeName;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <CalendarRange className="h-4 w-4" />
          Open times · {provider.providerName}
        </h3>
        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(v) => v && onViewChange(v as OfferedView)}
          className="rounded-lg border"
          aria-label="How to show the times"
        >
          <ToggleGroupItem value="week" data-view="week" className="px-3 text-xs">
            <CalendarDays className="mr-1.5 h-3.5 w-3.5" />
            Week
          </ToggleGroupItem>
          <ToggleGroupItem value="dates" data-view="dates" className="px-3 text-xs">
            <List className="mr-1.5 h-3.5 w-3.5" />
            Dates
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {provider.isTie && provider.tiedTypes.length > 0 && (
        <Select value={typeId ?? undefined} onValueChange={onPickType}>
          <SelectTrigger className="h-8 w-56" aria-label="Appointment type">
            <SelectValue placeholder="Appointment type" />
          </SelectTrigger>
          <SelectContent>
            {provider.tiedTypes.map((t) => (
              <SelectItem key={t.appointmentTypeId} value={t.appointmentTypeId}>
                {t.appointmentTypeName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <p className="text-xs text-muted-foreground">
        {typeName ?? 'Appointment'} at {provider.locationName}
        {view === 'dates' ? `, the next ${OPEN_DATES_DAYS} days` : ''}. Booked time, blocks and
        booking limits are already applied.
      </p>

      {!typeId ? (
        <p className="text-sm text-muted-foreground">No appointment type to look up times for.</p>
      ) : view === 'week' ? (
        <OfferedSlotsWeek
          // A new provider, type, location or clicked time opens afresh.
          key={`${provider.providerId}:${typeId}:${provider.locationId}:${focusSlot?.slotId ?? ''}`}
          focusSlot={focusSlot}
          providerId={provider.providerId}
          appointmentTypeId={typeId}
          locationId={provider.locationId}
          durationMinutes={types?.find((t) => t.id === typeId)?.durationMinutes}
          isNewPatient={isNewPatient}
          bodyClassName="pt-3"
        />
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Finding open times…
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">Couldn't load the open times.</p>
      ) : days.length === 0 ? (
        <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
          No open times in the next {OPEN_DATES_DAYS} days.
        </p>
      ) : (
        <ul className="divide-y rounded-md border" aria-label="Open dates">
          {days.map((day) => (
            <li key={day.key} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:gap-4">
              <span className="w-28 shrink-0 text-sm font-medium">{day.label}</span>
              <span className="flex flex-wrap gap-1.5">
                {day.times.map((time) => (
                  <button
                    key={time.key}
                    type="button"
                    data-slot-id={time.key}
                    onClick={() => onOpenSlot(time.slot)}
                    title="Show on the week calendar"
                    className="rounded-md bg-secondary px-2 py-0.5 text-xs tabular-nums transition-colors hover:bg-highlight hover:text-highlight-foreground"
                  >
                    {time.label}
                  </button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
      {view === 'dates' && data?.truncated && (
        <p className="text-xs text-muted-foreground">
          Some weeks have more open times than can be listed; the calendar shows them all.
        </p>
      )}
    </div>
  );
}

/** One open time in the Dates list, and the slot it opens on the calendar. */
interface OpenTime {
  key: string;
  label: string;
  slot: OfferableSlot;
}

/**
 * Open times grouped by the clinic's own calendar day, in order: a clinic's
 * 9:00 reads 9:00 wherever the viewer is.
 */
export function groupByDay(
  slots: readonly OfferableSlot[]
): Array<{ key: string; label: string; times: OpenTime[] }> {
  const days = new Map<string, { key: string; label: string; times: OpenTime[] }>();
  const sorted = [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));
  for (const slot of sorted) {
    const key = formatInTimeZone(slot.startTime, slot.locationTimezone, 'yyyy-MM-dd');
    const day = days.get(key) ?? {
      key,
      label: formatInTimeZone(slot.startTime, slot.locationTimezone, 'EEE, MMM d'),
      times: [],
    };
    day.times.push({
      key: slot.slotId,
      label: formatInTimeZone(slot.startTime, slot.locationTimezone, 'h:mm a'),
      slot,
    });
    days.set(key, day);
  }
  return [...days.values()];
}

/**
 * The type to look up a provider's times for: the one they would be booked
 * as, or, when they are tied across types, the one picked (the first until
 * someone picks).
 */
export function bookingTypeId(provider: RankedProvider, picked: string | undefined): string | null {
  if (provider.appointmentTypeId) return provider.appointmentTypeId;
  const tied = provider.tiedTypes.map((t) => t.appointmentTypeId);
  if (picked && tied.includes(picked)) return picked;
  return tied[0] ?? null;
}

/** How a pool line names the type: the one they would be booked as, or the tie. */
export function providerTypeLabel(provider: RankedProvider): string {
  if (provider.appointmentTypeName) return provider.appointmentTypeName;
  const tied = provider.tiedTypes.map((t) => t.appointmentTypeName);
  return tied.length > 0 ? `Tied: ${tied.join(' / ')}` : 'No type';
}
